import { useEffect } from 'react';

import { useGetFormalOffer } from '@api/lendingAPI/lapiHooks';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
    fetchPutFormalOffersCreditFacilityConfiguration,
    fetchPutFormalOffersMerchantCashAdvanceConfiguration,
    PutFormalOffersCreditFacilityConfigurationRequestBody
} from '@iwoca/lapi-client/edge';

import * as IwDataDogLogger from '../../../iwLogger/IwDataDogLogger';
import { useStateKey } from '../../../iwQueryClient/useStateKey';

type TOfferStep = NonNullable<
    NonNullable<
        NonNullable<PutFormalOffersCreditFacilityConfigurationRequestBody>['data']
    >['step']
>;

const putOfferStep = ({
    stateKey,
    formalOffer,
    step,
    amount,
    sweep
}: {
    stateKey: string;
    formalOffer: TFormalOffer;
    step: TOfferStep;
    amount: number;
    sweep: number | undefined;
}) => {
    const offerId = formalOffer.offer_id!;

    if (formalOffer.product_type === 'merchant_cash_advance') {
        return fetchPutFormalOffersMerchantCashAdvanceConfiguration({
            stateKey,
            offerId,
            body: {
                data: {
                    cash_amount: amount,
                    step,
                    revenue_sweep_fraction: sweep
                }
            }
        });
    }

    return fetchPutFormalOffersCreditFacilityConfiguration({
        stateKey,
        offerId,
        body: { data: { cash_amount: amount, step } }
    });
};

export const useWriteOfferStep = ({
    offerId,
    step,
    amount,
    sweep
}: {
    offerId: string | undefined;
    step: TOfferStep;
    amount: number | undefined;
    sweep?: number;
}): void => {
    const stateKey = useStateKey();
    const { formalOffer } = useGetFormalOffer(offerId);

    useEffect(() => {
        if (!formalOffer || amount === undefined || Number.isNaN(amount))
            return;

        putOfferStep({
            stateKey,
            formalOffer,
            step,
            amount,
            sweep
        })
            .then(() => {
                tracking.ampli.stepPersisted({
                    offer_id: formalOffer.offer_id!,
                    step
                });
            })
            .catch((error) =>
                IwDataDogLogger.logError(
                    'Failed to write offer flow memory step',
                    { tags: { reporter: 'offer_flow_memory', step } },
                    error as Error
                )
            );
    }, [stateKey, formalOffer, step, amount, sweep]);
};
